
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
    ArrowLeft, ArrowRight, BedDouble, CalendarDays,
    Check, CheckCircle2, ChevronLeft, ChevronRight,
    Clock3, CreditCard, Heart, Hotel, Image as ImageIcon,
    MapPin, Minus, Plus, ShieldCheck, Sparkles,
    Star, Users, Wifi, Wind, Coffee, Waves,
    Dumbbell, Car, Utensils, X, Tag, Quote,
    Maximize2, LoaderCircle
} from "lucide-react";

dayjs.extend(relativeTime);

const API = "https://dubaibudgetivehotels.com/api";
const HOTEL_IMG = "https://uaediscountedhotels.com/uploads/hotels";
const ROOM_IMG = "https://uaediscountedhotels.com/uploads/rooms";

const SERVICE_CHARGE = 0.10;
const VAT = 0.05;

const formatAED = (value) =>
    new Intl.NumberFormat("en-AE", {
        maximumFractionDigits: 2,
    }).format(Number(value) || 0);

const toArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.data?.data)) return data.data.data;
    return [];
};

const getImage = (value, base) => {
    const raw =
        typeof value === "object"
            ? value?.url || value?.path || value?.image
            : value;

    if (!raw || typeof raw !== "string") return "";

    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith("//")) return `https:${raw}`;
    if (raw.startsWith("/")) return raw;

    return `${base}/${raw}`;
};

const getImages = (value, fallback) => {
    let images = value;

    if (typeof images === "string") {
        try {
            images = JSON.parse(images);
        } catch {
            images = [images];
        }
    }

    const result = Array.isArray(images)
        ? images.filter(Boolean)
        : [];

    return result.length ? result : fallback ? [fallback] : [];
};

const cleanText = (value) =>
    String(value || "")
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const localToday = () => dayjs().format("YYYY-MM-DD");

function AmenityIcon({ name }) {
    const n = String(name).toLowerCase();

    if (n.includes("wifi")) return <Wifi size={19} />;
    if (n.includes("air")) return <Wind size={19} />;
    if (n.includes("breakfast") || n.includes("coffee"))
        return <Coffee size={19} />;
    if (n.includes("pool") || n.includes("beach"))
        return <Waves size={19} />;
    if (n.includes("gym") || n.includes("fitness"))
        return <Dumbbell size={19} />;
    if (n.includes("parking")) return <Car size={19} />;
    if (n.includes("restaurant")) return <Utensils size={19} />;

    return <Check size={19} />;
}

function SectionHeading({ eyebrow, title, description }) {
    return (
        <div className="mb-6">
            <p className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#168B43]">
                {eyebrow}
            </p>
            <h2 className="text-[26px] font-extrabold tracking-[-0.035em] text-[#17324D] sm:text-[32px]">
                {title}
            </h2>
            {description && (
                <p className="mt-2 text-[13px] leading-6 text-[#718096]">
                    {description}
                </p>
            )}
        </div>
    );
}

export default function HotelDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const [hotel, setHotel] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [bookingLoading, setBookingLoading] = useState(false);
    const [bookingError, setBookingError] = useState("");

    const [checkIn, setCheckIn] = useState("");
    const [checkOut, setCheckOut] = useState("");
    const [guests, setGuests] = useState(1);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");

    const [wishlist, setWishlist] = useState([]);
    const [showAllAmenities, setShowAllAmenities] = useState(false);
    const [showAllReviews, setShowAllReviews] = useState(false);
    const [activeImage, setActiveImage] = useState(null);

    const today = localToday();

    // LOAD HOTEL
    useEffect(() => {
        if (!slug) {
            setLoading(false);
            setError("Hotel not found.");
            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError("");
            setHotel(null);
            setRooms([]);
            setReviews([]);
            setSelectedRoom(null);

            try {
                const [hotelRes, roomRes, reviewRes] = await Promise.all([
                    axios.get(`${API}/hotels/${slug}`),
                    axios.get(`${API}/hotels/${slug}/rooms`),
                    axios
                        .get(`${API}/hotels/${slug}/reviews`)
                        .catch(() => ({ data: [] })),
                ]);

                if (cancelled) return;

                const hotelData =
                    hotelRes.data?.data &&
                        !Array.isArray(hotelRes.data.data)
                        ? hotelRes.data.data
                        : hotelRes.data;

                setHotel(hotelData);
                setRooms(toArray(roomRes.data));
                setReviews(toArray(reviewRes.data));
            } catch (err) {
                if (!cancelled) {
                    console.error(err);
                    setError("Unable to load this hotel.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [slug]);

    // WISHLIST
    useEffect(() => {
        const sync = () => {
            try {
                const modern = JSON.parse(
                    localStorage.getItem("dubai_hotel_wishlist") || "[]"
                );
                const legacy = JSON.parse(
                    localStorage.getItem("wishlist") || "[]"
                );

                setWishlist([
                    ...(Array.isArray(modern) ? modern : []),
                    ...(Array.isArray(legacy) ? legacy : []),
                ]);
            } catch {
                setWishlist([]);
            }
        };

        sync();
        window.addEventListener("hotel-wishlist-updated", sync);
        window.addEventListener("storage", sync);

        return () => {
            window.removeEventListener("hotel-wishlist-updated", sync);
            window.removeEventListener("storage", sync);
        };
    }, []);

    const isWishlisted = wishlist.some(
        (item) => String(item?.id) === String(hotel?.id)
    );

    const toggleWishlist = () => {
        if (!hotel) return;

        try {
            const key = "dubai_hotel_wishlist";
            const stored = JSON.parse(localStorage.getItem(key) || "[]");
            const current = Array.isArray(stored) ? stored : [];

            const legacy = JSON.parse(
                localStorage.getItem("wishlist") || "[]"
            );
            const oldItems = Array.isArray(legacy) ? legacy : [];

            const existing = [...current, ...oldItems].some(
                (item) => String(item?.id) === String(hotel.id)
            );

            const updated = existing
                ? current.filter(
                    (item) => String(item?.id) !== String(hotel.id)
                )
                : [...current, hotel];

            const updatedLegacy = oldItems.filter(
                (item) => String(item?.id) !== String(hotel.id)
            );

            localStorage.setItem(key, JSON.stringify(updated));
            localStorage.setItem(
                "wishlist",
                JSON.stringify(updatedLegacy)
            );

            setWishlist([...updated, ...updatedLegacy]);
            window.dispatchEvent(new Event("hotel-wishlist-updated"));
        } catch (err) {
            console.error("Wishlist error:", err);
        }
    };

    const amenities = useMemo(() => {
        if (!hotel?.amenities) return [];

        const items = Array.isArray(hotel.amenities)
            ? hotel.amenities
            : String(hotel.amenities).split(",");

        return items
            .map((item) =>
                typeof item === "object"
                    ? item?.name || item?.title || ""
                    : String(item)
            )
            .map((item) => item.trim())
            .filter(Boolean);
    }, [hotel]);

    const gallery = useMemo(
        () => getImages(hotel?.gallery, hotel?.image),
        [hotel]
    );

    // GALLERY KEYBOARD SUPPORT
    useEffect(() => {
        if (activeImage === null || !gallery.length) return;

        const keydown = (event) => {
            if (event.key === "Escape") setActiveImage(null);
            if (event.key === "ArrowRight") {
                setActiveImage((i) => (i + 1) % gallery.length);
            }
            if (event.key === "ArrowLeft") {
                setActiveImage(
                    (i) => (i - 1 + gallery.length) % gallery.length
                );
            }
        };

        const oldOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        document.addEventListener("keydown", keydown);

        return () => {
            document.body.style.overflow = oldOverflow;
            document.removeEventListener("keydown", keydown);
        };
    }, [activeImage, gallery.length]);

    const hotelRating = Number(hotel?.rating) || 0;

    // PRICING
    const originalPrice = (room) =>
        Number(room?.price ?? hotel?.price_per_night ?? 0);

    const bookingSeason = (room) => {
        const date = checkIn || today;
        const seasons = room?.seasonal_prices || [];

        return Array.isArray(seasons)
            ? seasons.find(
                (s) =>
                    date >= String(s.start_date).slice(0, 10) &&
                    date <= String(s.end_date).slice(0, 10)
            ) || null
            : null;
    };

    const pricing = (room) => {
        if (!room) {
            return {
                original: 0, price: 0, saved: 0,
                percent: 0, season: null, final: 0,
            };
        }

        const season = bookingSeason(room);
        const original = Number(
            season?.price ?? originalPrice(room)
        );

        const discount = Number(room.discount_price || 0);
        const price = discount > 0 ? discount : original;

        const saved = Math.max(original - price, 0);
        const percent = original > 0
            ? Math.round((saved / original) * 100)
            : 0;

        const tourismFee = hotelRating === 5 ? 20 : 10;

        const final = Math.round(
            price +
            price * SERVICE_CHARGE +
            price * VAT +
            tourismFee
        );

        return {
            original, price, saved, percent,
            season, final, tourismFee,
        };
    };

    const nights =
        checkIn && checkOut && dayjs(checkOut).isAfter(dayjs(checkIn))
            ? dayjs(checkOut).diff(dayjs(checkIn), "day")
            : 1;

    const selectedPricing = pricing(selectedRoom);
    const bookingTotal = selectedRoom
        ? selectedPricing.final * nights
        : 0;

    const minRoomPrice = rooms.length
        ? Math.min(...rooms.map((room) => pricing(room).final))
        : null;

    // BOOKING
    const handleBooking = async (event) => {
        event.preventDefault();
        setBookingError("");

        if (!selectedRoom) {
            setBookingError("Please select a room first.");
            return;
        }

        if (!checkIn || !checkOut || !name || !email || !phone) {
            setBookingError("Please complete all booking fields.");
            return;
        }

        if (
            checkIn < localToday() ||
            !dayjs(checkOut).isAfter(dayjs(checkIn))
        ) {
            setBookingError("Please select valid check-in and check-out dates.");
            return;
        }

        if (
            selectedRoom.capacity &&
            guests > Number(selectedRoom.capacity)
        ) {
            setBookingError("This room cannot accommodate the selected guests.");
            return;
        }

        if (!executeRecaptcha) {
            setBookingError("Captcha is not ready. Please try again.");
            return;
        }

        try {
            setBookingLoading(true);

            const captcha = await executeRecaptcha("hotel_booking");
            const season = bookingSeason(selectedRoom);

            let user = null;
            try {
                user = JSON.parse(localStorage.getItem("user") || "null");
            } catch { }

            const response = await axios.post(
                `${API}/hotel-payment`,
                {
                    user_id: user?.id || null,
                    hotel_id: hotel.id,
                    room_id: selectedRoom.id,
                    room_name:
                        selectedRoom.name ||
                        `Room ${selectedRoom.room_number || ""}`.trim(),
                    room_price: selectedPricing.price,
                    discount_price: selectedRoom.discount_price || null,
                    season_name: season?.season_name || null,
                    season_start: season?.start_date || null,
                    season_end: season?.end_date || null,
                    name,
                    email,
                    phone,
                    check_in: checkIn,
                    check_out: checkOut,
                    guests: Number(guests),
                    nights,
                    total_amount: bookingTotal,
                    captcha,
                }
            );

            const paymentUrl = response.data?.payment_url;

            if (paymentUrl) {
                const parsed = new URL(paymentUrl, window.location.origin);

                if (!["http:", "https:"].includes(parsed.protocol)) {
                    throw new Error("Invalid payment URL.");
                }

                window.location.assign(parsed.href);
            } else {
                setBookingError("Payment link was not received.");
            }
        } catch (err) {
            console.error(err);
            setBookingError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                "Booking failed. Please try again."
            );
        } finally {
            setBookingLoading(false);
        }
    };

    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    // LOADING
    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5FAF7] pt-28">
                <div className="mx-auto max-w-[1536px] animate-pulse space-y-6 px-4 sm:px-6 lg:px-8">
                    <div className="h-12 w-2/3 rounded-xl bg-[#DFEBE3]" />
                    <div className="h-[420px] rounded-2xl bg-[#DFEBE3]" />
                    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
                        <div className="h-[360px] rounded-2xl bg-[#DFEBE3]" />
                        <div className="h-[360px] rounded-2xl bg-[#DFEBE3]" />
                    </div>
                </div>
            </div>
        );
    }

    // ERROR
    if (error || !hotel) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#F5FAF7] px-4">
                <div className="max-w-md rounded-2xl border border-[#E0ECE4] bg-white p-10 text-center">
                    <Hotel size={42} className="mx-auto text-[#168B43]" />
                    <h1 className="mt-5 text-2xl font-extrabold text-[#17324D]">
                        Hotel Not Found
                    </h1>
                    <p className="mt-3 text-sm text-[#718096]">
                        {error || "This hotel is currently unavailable."}
                    </p>
                    <Link
                        to="/hotels"
                        className="mt-6 inline-flex rounded-xl bg-[#168B43] px-6 py-3 font-bold text-white"
                    >
                        Browse Hotels
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{hotel.meta_title || hotel.name}</title>
                <meta
                    name="description"
                    content={cleanText(
                        hotel.meta_description || hotel.description
                    )}
                />
            </Helmet>

            <main className="min-h-screen bg-[#F5FAF7] pb-28 pt-24 text-[#17324D]">
                {/* HEADER */}
                <section className="border-b border-[#E4EEE7] bg-white">
                    <div className="mx-auto max-w-[1536px] px-4 py-8 sm:px-6 lg:px-8">
                        <div className="mb-7 flex flex-wrap items-center justify-between gap-3">
                            <nav className="flex flex-wrap items-center gap-2 text-xs text-[#718096]">
                                <Link to="/" className="hover:text-[#168B43]">
                                    Home
                                </Link>
                                <ChevronRight size={13} />
                                <Link to="/hotels" className="hover:text-[#168B43]">
                                    Hotels
                                </Link>
                                <ChevronRight size={13} />
                                <span className="font-bold text-[#17324D]">
                                    {hotel.name}
                                </span>
                            </nav>

                            <div className="flex gap-2">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="flex items-center gap-2 rounded-full border border-[#E0ECE4] px-4 py-2 text-xs font-bold hover:text-[#168B43]"
                                >
                                    <ArrowLeft size={15} /> Back
                                </button>
                                <button
                                    onClick={toggleWishlist}
                                    aria-label="Toggle wishlist"
                                    aria-pressed={isWishlisted}
                                    className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E0ECE4]"
                                >
                                    <Heart
                                        size={19}
                                        className={
                                            isWishlisted
                                                ? "fill-red-500 text-red-500"
                                                : "text-[#17324D]"
                                        }
                                    />
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-end justify-between gap-6">
                            <div>
                                <div className="mb-3 flex flex-wrap gap-2">
                                    <span className="rounded-full bg-[#E8F7EE] px-3 py-1.5 text-[11px] font-bold text-[#168B43]">
                                        <MapPin size={12} className="mr-1 inline" />
                                        Dubai, UAE
                                    </span>

                                    {hotelRating > 0 && (
                                        <span className="rounded-full bg-[#FFF7E6] px-3 py-1.5 text-[11px] font-bold text-[#17324D]">
                                            <Star
                                                size={12}
                                                className="mr-1 inline fill-[#F3AE2C] text-[#F3AE2C]"
                                            />
                                            {hotelRating.toFixed(1)} Rating
                                        </span>
                                    )}
                                </div>

                                <h1 className="max-w-4xl text-[34px] font-extrabold leading-tight tracking-[-0.04em] sm:text-[46px]">
                                    {hotel.name}
                                </h1>

                                {hotel.location && (
                                    <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(hotel.location)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-3 inline-flex items-center gap-2 text-sm text-[#718096] hover:text-[#168B43]"
                                    >
                                        <MapPin size={16} className="text-[#168B43]" />
                                        {hotel.location}
                                        <ArrowRight size={14} />
                                    </a>
                                )}
                            </div>

                            <button
                                onClick={() => scrollTo("rooms")}
                                className="inline-flex items-center gap-2 rounded-xl bg-[#168B43] px-6 py-3.5 text-sm font-bold text-white hover:bg-[#087D37]"
                            >
                                Explore Rooms
                                <ArrowRight size={17} />
                            </button>
                        </div>
                    </div>
                </section>

                {/* PHOTO GALLERY */}
                <section className="mx-auto max-w-[1536px] px-4 pt-7 sm:px-6 lg:px-8">
                    <div className="grid gap-3 overflow-hidden rounded-[20px] md:grid-cols-[1.7fr_0.8fr]">
                        <div
                            className="relative h-[290px] cursor-pointer overflow-hidden bg-[#E1ECE5] sm:h-[410px] lg:h-[480px]"
                            onClick={() => gallery.length && setActiveImage(0)}
                        >
                            {gallery.length ? (
                                <img
                                    src={getImage(gallery[0], HOTEL_IMG)}
                                    alt={hotel.name}
                                    className="h-full w-full object-cover transition duration-500 hover:scale-105"
                                />
                            ) : (
                                <div className="flex h-full items-center justify-center text-[#718096]">
                                    <ImageIcon size={36} />
                                </div>
                            )}

                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#064E3B]/70 to-transparent p-5">
                                <span className="inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-xs font-bold text-[#17324D]">
                                    <Maximize2 size={14} />
                                    View Hotel Photos
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 md:grid-cols-1 md:grid-rows-2">
                            {[1, 2].map((index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() =>
                                        gallery[index] && setActiveImage(index)
                                    }
                                    disabled={!gallery[index]}
                                    className="relative min-h-[135px] overflow-hidden bg-[#E1ECE5] disabled:cursor-default"
                                >
                                    {gallery[index] ? (
                                        <>
                                            <img
                                                src={getImage(gallery[index], HOTEL_IMG)}
                                                alt={`${hotel.name} photo ${index + 1}`}
                                                className="h-full w-full object-cover transition duration-500 hover:scale-105"
                                            />
                                            {index === 2 && gallery.length > 3 && (
                                                <span className="absolute inset-0 flex items-center justify-center bg-[#064E3B]/55 text-lg font-extrabold text-white">
                                                    +{gallery.length - 3} More Photos
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <div className="flex h-full items-center justify-center text-sm text-[#8DA39A]">
                                            <ImageIcon size={25} />
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* QUICK FACTS */}
                    <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                        {[
                            { icon: MapPin, label: "Location", value: hotel.location || "Dubai, UAE" },
                            { icon: BedDouble, label: "Room Options", value: `${rooms.length} Rooms` },
                            { icon: Users, label: "Travelers", value: `${guests} Guest${guests > 1 ? "s" : ""}` },
                            { icon: CalendarDays, label: "Your Stay", value: checkIn && checkOut ? `${nights} Nights` : "Select Dates" },
                        ].map((item) => (
                            <div
                                key={item.label}
                                className="flex items-center gap-3 rounded-xl border border-[#E2ECE6] bg-white p-4"
                            >
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                                    <item.icon size={21} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">
                                        {item.label}
                                    </p>
                                    <p className="mt-1 truncate text-[13px] font-extrabold text-[#17324D]">
                                        {item.value}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* CONTENT */}
                <section className="mx-auto mt-10 max-w-[1536px] px-4 sm:px-6 lg:px-8">
                    <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_390px]">
                        <div className="min-w-0 space-y-9">
                            {/* ABOUT */}
                            <section className="rounded-[20px] border border-[#E2ECE6] bg-white p-6 sm:p-8">
                                <SectionHeading
                                    eyebrow="Discover Your Stay"
                                    title="About This Hotel"
                                    description="Get to know your hotel before you book."
                                />

                                <p className="whitespace-pre-line text-[14px] leading-8 text-[#64748B]">
                                    {cleanText(hotel.description) ||
                                        "Discover accommodation options for your Dubai visit."}
                                </p>

                                {amenities.length > 0 && (
                                    <div className="mt-8 border-t border-[#EAF1EC] pt-7">
                                        <div className="mb-5 flex items-center justify-between gap-3">
                                            <h3 className="text-xl font-extrabold text-[#17324D]">
                                                Hotel Amenities
                                            </h3>

                                            {amenities.length > 8 && (
                                                <button
                                                    onClick={() => setShowAllAmenities((v) => !v)}
                                                    className="text-xs font-bold text-[#168B43]"
                                                >
                                                    {showAllAmenities ? "Show Less" : "View All"}
                                                </button>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                                            {(showAllAmenities
                                                ? amenities
                                                : amenities.slice(0, 8)
                                            ).map((amenity, i) => (
                                                <div
                                                    key={`${amenity}-${i}`}
                                                    className="flex items-center gap-3 rounded-xl border border-[#E6F0E9] bg-[#F8FBF9] p-3"
                                                >
                                                    <span className="text-[#168B43]">
                                                        <AmenityIcon name={amenity} />
                                                    </span>
                                                    <span className="text-xs font-semibold text-[#17324D]">
                                                        {amenity}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {(hotel.check_in_time || hotel.check_out_time) && (
                                    <div className="mt-7 grid gap-3 border-t border-[#EAF1EC] pt-6 sm:grid-cols-2">
                                        {[
                                            ["Check-in", hotel.check_in_time],
                                            ["Check-out", hotel.check_out_time],
                                        ].filter(([, value]) => value).map(([label, value]) => (
                                            <div key={label} className="flex items-center gap-3 rounded-xl bg-[#F5FAF7] p-4">
                                                <Clock3 size={20} className="text-[#168B43]" />
                                                <div>
                                                    <p className="text-xs text-[#718096]">{label}</p>
                                                    <p className="font-bold">{value}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>

                            {/* ROOMS */}
                            <section id="rooms" className="scroll-mt-28">
                                <SectionHeading
                                    eyebrow="Find Your Comfort"
                                    title="Available Rooms"
                                    description="Compare room options and select the one that suits your stay."
                                />

                                <div className="space-y-5">
                                    {rooms.map((room) => {
                                        const images = getImages(room.images, room.image);
                                        const price = pricing(room);
                                        const selected =
                                            selectedRoom?.id === room.id;

                                        return (
                                            <article
                                                key={room.id}
                                                className={`overflow-hidden rounded-[20px] border bg-white transition-all ${selected
                                                    ? "border-[#168B43] shadow-[0_12px_35px_rgba(22,139,67,0.12)] ring-2 ring-[#168B43]/10"
                                                    : "border-[#E2ECE6] hover:border-[#B9DEC9]"
                                                    }`}
                                            >
                                                <div className="grid md:grid-cols-[250px_minmax(0,1fr)] xl:grid-cols-[290px_minmax(0,1fr)]">
                                                    <div className="relative min-h-[240px] bg-[#E2ECE6]">
                                                        {images.length ? (
                                                            <img
                                                                src={getImage(images[0], ROOM_IMG)}
                                                                alt={room.name || "Hotel room"}
                                                                className="absolute inset-0 h-full w-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="flex h-full min-h-[240px] items-center justify-center text-[#94A3B8]">
                                                                <BedDouble size={35} />
                                                            </div>
                                                        )}

                                                        {price.percent > 0 && (
                                                            <span className="absolute left-4 top-4 rounded-full bg-[#168B43] px-3 py-1.5 text-[11px] font-bold text-white">
                                                                {price.percent}% OFF
                                                            </span>
                                                        )}

                                                        {selected && (
                                                            <span className="absolute bottom-4 left-4 flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-[#168B43]">
                                                                <CheckCircle2 size={14} /> Selected
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="flex flex-col justify-between p-5 sm:p-6">
                                                        <div>
                                                            <div className="flex flex-wrap justify-between gap-4">
                                                                <div>
                                                                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#168B43]">
                                                                        {room.room_number
                                                                            ? `Room ${room.room_number}`
                                                                            : "Room Option"}
                                                                    </p>
                                                                    <h3 className="mt-2 text-[22px] font-extrabold text-[#17324D]">
                                                                        {room.name || "Hotel Room"}
                                                                    </h3>

                                                                    <div className="mt-3 flex flex-wrap gap-4 text-xs text-[#718096]">
                                                                        {room.capacity && (
                                                                            <span className="flex items-center gap-1.5">
                                                                                <Users size={15} className="text-[#168B43]" />
                                                                                Up to {room.capacity} Guests
                                                                            </span>
                                                                        )}
                                                                        {room.beds_count && (
                                                                            <span className="flex items-center gap-1.5">
                                                                                <BedDouble size={15} className="text-[#168B43]" />
                                                                                {room.beds_count} Beds
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="sm:text-right">
                                                                    <p className="text-[10px] font-bold uppercase text-[#94A3B8]">
                                                                        Per Night
                                                                    </p>
                                                                    <p className="mt-1 text-2xl font-extrabold text-[#168B43]">
                                                                        <span className="mr-1 text-sm">AED</span>
                                                                        {formatAED(price.price)}
                                                                    </p>
                                                                    {price.saved > 0 && (
                                                                        <p className="text-xs text-[#94A3B8] line-through">
                                                                            AED {formatAED(price.original)}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {price.season && (
                                                                <div className="mt-4 rounded-lg bg-[#E8F7EE] px-3 py-2 text-xs text-[#168B43]">
                                                                    <Tag size={13} className="mr-1 inline" />
                                                                    {price.season.season_name || "Seasonal pricing"}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="mt-6 flex flex-wrap gap-3 border-t border-[#EAF1EC] pt-4">
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    navigate(`/hotels/${hotel.slug}/${room.id}`)
                                                                }
                                                                className="flex-1 rounded-xl border border-[#DCEAE1] px-4 py-3 text-xs font-bold text-[#17324D] hover:border-[#168B43]"
                                                            >
                                                                Room Details
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setSelectedRoom(room);
                                                                    scrollTo("booking-panel");
                                                                }}
                                                                className={`flex-1 rounded-xl px-4 py-3 text-xs font-extrabold ${selected
                                                                    ? "bg-[#064E3B] text-white"
                                                                    : "bg-[#168B43] text-white hover:bg-[#087D37]"
                                                                    }`}
                                                            >
                                                                {selected ? "Room Selected ✓" : "Select Room"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </article>
                                        );
                                    })}

                                    {!rooms.length && (
                                        <div className="rounded-2xl border border-[#E2ECE6] bg-white p-12 text-center">
                                            <BedDouble size={36} className="mx-auto text-[#168B43]" />
                                            <h3 className="mt-4 text-lg font-bold">
                                                No Rooms Available
                                            </h3>
                                            <p className="mt-2 text-sm text-[#718096]">
                                                Please check back later.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* REVIEWS */}
                            <section className="rounded-[20px] border border-[#E2ECE6] bg-white p-6 sm:p-8">
                                <SectionHeading
                                    eyebrow="Guest Experiences"
                                    title="Guest Reviews"
                                    description="Feedback shared by guests."
                                />

                                {reviews.length ? (
                                    <>
                                        <div className="mb-6 flex items-center gap-3 rounded-xl bg-[#F5FAF7] p-4">
                                            <Star size={23} className="fill-[#F3AE2C] text-[#F3AE2C]" />
                                            <span className="text-2xl font-extrabold">
                                                {(
                                                    reviews.reduce(
                                                        (sum, review) => sum + Number(review.rating || 0),
                                                        0
                                                    ) / reviews.length
                                                ).toFixed(1)}
                                            </span>
                                            <span className="text-xs text-[#718096]">
                                                Based on {reviews.length} reviews
                                            </span>
                                        </div>

                                        <div className="space-y-4">
                                            {(showAllReviews ? reviews : reviews.slice(0, 4)).map(
                                                (review, index) => {
                                                    const guestName =
                                                        review.name ||
                                                        review.guest_name ||
                                                        "Guest";
                                                    const rating = Math.max(
                                                        0,
                                                        Math.min(5, Number(review.rating) || 0)
                                                    );

                                                    return (
                                                        <article
                                                            key={review.id || index}
                                                            className="rounded-xl border border-[#EAF1EC] p-5"
                                                        >
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E8F7EE] font-extrabold text-[#168B43]">
                                                                        {guestName.charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <div>
                                                                        <h4 className="text-sm font-extrabold">
                                                                            {guestName}
                                                                        </h4>
                                                                        {review.created_at && (
                                                                            <p className="mt-1 text-[11px] text-[#94A3B8]">
                                                                                {dayjs(review.created_at).fromNow()}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <span className="flex items-center gap-1 text-sm font-bold">
                                                                    <Star size={15} className="fill-[#F3AE2C] text-[#F3AE2C]" />
                                                                    {rating.toFixed(1)}
                                                                </span>
                                                            </div>

                                                            <div className="mt-4 flex gap-3 border-t border-[#EAF1EC] pt-4">
                                                                <Quote size={17} className="shrink-0 text-[#168B43]" />
                                                                <p className="text-sm leading-7 text-[#64748B]">
                                                                    {review.comment}
                                                                </p>
                                                            </div>
                                                        </article>
                                                    );
                                                }
                                            )}
                                        </div>

                                        {reviews.length > 4 && (
                                            <button
                                                onClick={() => setShowAllReviews((v) => !v)}
                                                className="mt-5 w-full rounded-xl border border-[#CFE6D8] py-3 text-sm font-bold text-[#168B43]"
                                            >
                                                {showAllReviews ? "Show Fewer" : "View All Reviews"}
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <div className="py-10 text-center">
                                        <Star size={32} className="mx-auto text-[#168B43]" />
                                        <h3 className="mt-4 font-bold">No Reviews Yet</h3>
                                        <p className="mt-2 text-sm text-[#718096]">
                                            Guest reviews will appear here when available.
                                        </p>
                                    </div>
                                )}
                            </section>
                        </div>

                        {/* BOOKING PANEL */}
                        <aside id="booking-panel" className="scroll-mt-28 lg:sticky lg:top-28">
                            <div className="overflow-hidden rounded-[20px] border border-[#DCEAE1] bg-white shadow-[0_15px_45px_rgba(6,78,59,0.09)]">
                                <div className="bg-gradient-to-br from-[#064E3B] to-[#168B43] p-6 text-white">
                                    <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#B6F2CB]">
                                        Reserve Your Stay
                                    </p>
                                    <h2 className="mt-2 text-2xl font-extrabold !text-white">
                                        Book This Hotel
                                    </h2>
                                    <p className="mt-2 text-xs leading-5 text-white/80">
                                        Select your dates, room and guest details.
                                    </p>
                                </div>

                                <form onSubmit={handleBooking} className="space-y-5 p-5 sm:p-6">
                                    <div className="grid grid-cols-2 gap-3">
                                        <label className="block">
                                            <span className="mb-2 block text-[11px] font-bold text-[#64748B]">
                                                Check-in
                                            </span>
                                            <input
                                                type="date"
                                                required
                                                min={today}
                                                value={checkIn}
                                                onChange={(e) => {
                                                    setCheckIn(e.target.value);
                                                    if (checkOut <= e.target.value) setCheckOut("");
                                                }}
                                                className="w-full min-w-0 rounded-xl border border-[#DCEAE1] px-2 py-3 text-xs outline-none focus:border-[#168B43]"
                                            />
                                        </label>
                                        <label className="block">
                                            <span className="mb-2 block text-[11px] font-bold text-[#64748B]">
                                                Check-out
                                            </span>
                                            <input
                                                type="date"
                                                required
                                                min={
                                                    checkIn
                                                        ? dayjs(checkIn).add(1, "day").format("YYYY-MM-DD")
                                                        : dayjs(today).add(1, "day").format("YYYY-MM-DD")
                                                }
                                                value={checkOut}
                                                onChange={(e) => setCheckOut(e.target.value)}
                                                className="w-full min-w-0 rounded-xl border border-[#DCEAE1] px-2 py-3 text-xs outline-none focus:border-[#168B43]"
                                            />
                                        </label>
                                    </div>

                                    <div>
                                        <span className="mb-2 block text-[11px] font-bold text-[#64748B]">
                                            Number of Guests
                                        </span>
                                        <div className="flex items-center justify-between rounded-xl border border-[#DCEAE1] px-4 py-2.5">
                                            <span className="flex items-center gap-2 text-sm font-semibold">
                                                <Users size={17} className="text-[#168B43]" />
                                                {guests} Guest{guests > 1 ? "s" : ""}
                                            </span>
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setGuests((v) => Math.max(1, v - 1))}
                                                    className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#E8F7EE] text-[#168B43]"
                                                >
                                                    <Minus size={15} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setGuests((v) => v + 1)}
                                                    className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#E8F7EE] text-[#168B43]"
                                                >
                                                    <Plus size={15} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="border-t border-[#EAF1EC] pt-5">
                                        <h3 className="mb-4 text-sm font-extrabold text-[#17324D]">
                                            Guest Information
                                        </h3>
                                        <div className="space-y-3">
                                            <input
                                                type="text"
                                                required
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                placeholder="Full Name"
                                                className="w-full rounded-xl border border-[#DCEAE1] px-4 py-3 text-sm outline-none focus:border-[#168B43]"
                                            />
                                            <input
                                                type="email"
                                                required
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="Email Address"
                                                className="w-full rounded-xl border border-[#DCEAE1] px-4 py-3 text-sm outline-none focus:border-[#168B43]"
                                            />
                                            <input
                                                type="tel"
                                                required
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                placeholder="Phone Number"
                                                className="w-full rounded-xl border border-[#DCEAE1] px-4 py-3 text-sm outline-none focus:border-[#168B43]"
                                            />
                                        </div>
                                    </div>

                                    {selectedRoom ? (
                                        <div className="rounded-xl border border-[#CFE6D8] bg-[#F5FAF7] p-4">
                                            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[#168B43]">
                                                Selected Room
                                            </p>
                                            <h3 className="text-sm font-extrabold">
                                                {selectedRoom.name || "Hotel Room"}
                                            </h3>

                                            <div className="mt-4 space-y-2.5 text-xs text-[#64748B]">
                                                <div className="flex justify-between">
                                                    <span>Room Rate</span>
                                                    <span>AED {formatAED(selectedPricing.price)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>Service Charge (10%)</span>
                                                    <span>AED {formatAED(selectedPricing.price * SERVICE_CHARGE)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>VAT (5%)</span>
                                                    <span>AED {formatAED(selectedPricing.price * VAT)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>Tourism Fee / Night</span>
                                                    <span>AED {formatAED(selectedPricing.tourismFee)}</span>
                                                </div>
                                                <div className="flex justify-between border-t border-[#DCEAE1] pt-3 font-bold text-[#17324D]">
                                                    <span>Per Night</span>
                                                    <span>AED {formatAED(selectedPricing.final)}</span>
                                                </div>
                                                <div className="flex justify-between border-t border-[#DCEAE1] pt-3 text-sm font-extrabold text-[#17324D]">
                                                    <span>Total ({nights} Nights)</span>
                                                    <span className="text-[#168B43]">
                                                        AED {formatAED(bookingTotal)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="rounded-xl border border-dashed border-[#B9DEC9] bg-[#F5FAF7] p-5 text-center">
                                            <BedDouble size={24} className="mx-auto text-[#168B43]" />
                                            <p className="mt-2 text-xs font-semibold text-[#64748B]">
                                                Select a room to see your booking total.
                                            </p>
                                        </div>
                                    )}

                                    {bookingError && (
                                        <p role="alert" className="rounded-lg bg-red-50 p-3 text-xs text-red-700">
                                            {bookingError}
                                        </p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={!selectedRoom || bookingLoading}
                                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#168B43] py-4 text-sm font-extrabold text-white transition hover:bg-[#087D37] disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {bookingLoading ? (
                                            <>
                                                <LoaderCircle size={17} className="animate-spin" />
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                Confirm & Book Now
                                                <ArrowRight size={17} />
                                            </>
                                        )}
                                    </button>

                                    <p className="flex items-center justify-center gap-2 text-center text-[11px] text-[#94A3B8]">
                                        <ShieldCheck size={15} />
                                        Continue to payment
                                    </p>
                                </form>
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-3">
                                <div className="rounded-xl border border-[#E2ECE6] bg-white p-4 text-center">
                                    <ShieldCheck size={21} className="mx-auto text-[#168B43]" />
                                    <p className="mt-2 text-xs font-bold">Booking Assistance</p>
                                </div>
                                <div className="rounded-xl border border-[#E2ECE6] bg-white p-4 text-center">
                                    <CreditCard size={21} className="mx-auto text-[#168B43]" />
                                    <p className="mt-2 text-xs font-bold">Payment Options</p>
                                </div>
                            </div>
                        </aside>
                    </div>
                </section>
            </main>

            {/* FULLSCREEN GALLERY */}
            {activeImage !== null && gallery.length > 0 && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Hotel gallery"
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 p-4"
                >
                    <button
                        type="button"
                        onClick={() => setActiveImage(null)}
                        className="absolute right-5 top-5 rounded-full bg-white/15 p-3 text-white"
                        aria-label="Close gallery"
                    >
                        <X size={23} />
                    </button>

                    <span className="absolute left-5 top-6 text-sm font-bold text-white">
                        {activeImage + 1} / {gallery.length}
                    </span>

                    {gallery.length > 1 && (
                        <button
                            type="button"
                            onClick={() =>
                                setActiveImage((i) => (i - 1 + gallery.length) % gallery.length)
                            }
                            className="absolute left-3 rounded-full bg-white/15 p-3 text-white sm:left-8"
                            aria-label="Previous image"
                        >
                            <ChevronLeft size={24} />
                        </button>
                    )}

                    <img
                        src={getImage(gallery[activeImage], HOTEL_IMG)}
                        alt={`${hotel.name} gallery`}
                        className="max-h-[85vh] max-w-[85vw] object-contain"
                    />

                    {gallery.length > 1 && (
                        <button
                            type="button"
                            onClick={() =>
                                setActiveImage((i) => (i + 1) % gallery.length)
                            }
                            className="absolute right-3 rounded-full bg-white/15 p-3 text-white sm:right-8"
                            aria-label="Next image"
                        >
                            <ChevronRight size={24} />
                        </button>
                    )}
                </div>
            )}

            {/* MOBILE BOOKING BAR */}
            <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-[#E2ECE6] bg-white p-3 shadow-xl lg:hidden">
                <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
                    <div>
                        <p className="text-[10px] font-bold uppercase text-[#94A3B8]">
                            {selectedRoom ? "Selected Room" : "Starting From"}
                        </p>
                        <p className="mt-1 text-sm font-extrabold text-[#168B43]">
                            {selectedRoom
                                ? `AED ${formatAED(selectedPricing.final)} / night`
                                : minRoomPrice !== null
                                    ? `AED ${formatAED(minRoomPrice)} / night`
                                    : "View Rooms"}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() =>
                            scrollTo(selectedRoom ? "booking-panel" : "rooms")
                        }
                        className="rounded-xl bg-[#168B43] px-5 py-3 text-xs font-extrabold text-white"
                    >
                        {selectedRoom ? "Reserve Now" : "Choose Room"}
                    </button>
                </div>
            </div>
        </>
    );
}
